import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import monitoringService from '../../services/monitoringService';
import projectService from '../../services/projectService';
import DatePickerField from '../../components/common/DatePickerField';
import EvidenceUploader from '../../components/common/EvidenceUploader';
import { formatCategory } from '../inspections/categoryOptions';
import MonitoringNav from './MonitoringNav';
import './Monitoring.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  projectId: '',
  parameterId: '',
  entryDate: todayIso(),
  numberValue: '',
  textValue: '',
  booleanValue: 'true',
  selectValue: '',
  notes: '',
};

const DailyLog = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [evidenceEntry, setEvidenceEntry] = useState(null); // the entry whose evidence uploader is open

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter !== 'all') params.projectId = projectFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const data = await monitoringService.getEntries(params);
      setEntries(data.entries || []);
    } catch (error) {
      console.error('Failed to fetch monitoring entries:', error);
      toast.error(error.response?.data?.error || 'Failed to load daily log entries');
    } finally {
      setLoading(false);
    }
  }, [projectFilter, categoryFilter]);

  const fetchLookups = useCallback(async () => {
    try {
      const [projectData, parameterData] = await Promise.all([
        projectService.getAll(),
        monitoringService.getParameters({ isActive: 'true' }),
      ]);
      setProjects(projectData.projects || []);
      setParameters(parameterData.parameters || []);
    } catch (error) {
      console.error('Failed to load projects/parameters:', error);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    if (location.pathname.endsWith('/new')) {
      setForm(emptyForm);
      setShowModal(true);
    }
  }, [location.pathname]);

  const categories = useMemo(() => {
    const set = new Set(parameters.map((p) => p.category));
    return Array.from(set);
  }, [parameters]);

  const availableParameters = useMemo(() => {
    if (!form.projectId) return parameters;
    return parameters.filter((p) => !p.projectId || p.projectId === form.projectId);
  }, [parameters, form.projectId]);

  const selectedParameter = parameters.find((p) => p.id === form.parameterId);

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    if (location.pathname.endsWith('/new')) {
      navigate('/monitoring/records', { replace: true });
    }
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId || !form.parameterId) {
      toast.error('Please select a project and a parameter');
      return;
    }

    let value;
    switch (selectedParameter?.dataType) {
      case 'number':
        if (form.numberValue === '') {
          toast.error('Please enter a value');
          return;
        }
        value = { number: parseFloat(form.numberValue) };
        break;
      case 'boolean':
        value = { boolean: form.booleanValue === 'true' };
        break;
      case 'select':
        if (!form.selectValue) {
          toast.error('Please choose an option');
          return;
        }
        value = { select: form.selectValue };
        break;
      default:
        if (!form.textValue.trim()) {
          toast.error('Please enter a value');
          return;
        }
        value = { text: form.textValue.trim() };
    }

    try {
      setSubmitting(true);
      await monitoringService.submitEntry({
        projectId: form.projectId,
        parameterId: form.parameterId,
        entryDate: form.entryDate,
        value,
        notes: form.notes || null,
      });
      toast.success('Entry submitted successfully');
      closeModal();
      fetchEntries();
    } catch (error) {
      console.error('Failed to submit entry:', error);
      toast.error(error.response?.data?.error || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const formatValue = (entry) => {
    const v = entry.value || {};
    if (v.number !== undefined) return `${v.number}${entry.parameter?.unit ? ` ${entry.parameter.unit}` : ''}`;
    if (v.text !== undefined) return v.text;
    if (v.boolean !== undefined) return v.boolean ? 'Yes' : 'No';
    if (v.select !== undefined) return v.select;
    return '—';
  };


  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-shield-alt"></i> Monitoring</h1>
          <p className="header-subtitle">Daily and periodic data across every monitored area</p>
        </div>
        <button
          className="btn-primary-green"
          onClick={() => { setForm(emptyForm); setShowModal(true); }}
          disabled={parameters.length === 0}
        >
          <i className="fas fa-plus"></i> Add Entry
        </button>
      </div>

      <MonitoringNav />

      {parameters.length === 0 && !loading && (
        <div className="empty-state" style={{ marginBottom: '1.25rem' }}>
          <i className="fas fa-info-circle"></i>
          <h3>No monitoring parameters configured yet</h3>
          <p>Set up what you want to track first.</p>
          <Link to="/monitoring/parameters/new" className="btn-text">
            <i className="fas fa-plus"></i> Configure a Parameter
          </Link>
        </div>
      )}

      <div className="monitoring-controls">
        <select className="filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{formatCategory(c)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading daily log...</h3>
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-clipboard-list"></i>
          <h3>No entries yet</h3>
          <p>Submissions will appear here once your team starts logging data.</p>
        </div>
      ) : (
        <div className="monitoring-list">
          {entries.map((entry) => (
            <div key={entry.id} className="monitoring-card">
              <div className="monitoring-card-main">
                <h4>{entry.parameter?.name || 'Parameter'}: {formatValue(entry)}</h4>
                <div className="monitoring-card-meta">
                  <span className="category-badge">{formatCategory(entry.parameter?.category)}</span>
                  <span><i className="fas fa-project-diagram"></i> {entry.project?.name}</span>
                  <span><i className="fas fa-calendar"></i> {new Date(entry.entryDate).toLocaleDateString()}</span>
                  {entry.submitter && (
                    <span><i className="fas fa-user"></i> {entry.submitter.firstName} {entry.submitter.lastName}</span>
                  )}
                  {entry.notes && <span><i className="fas fa-sticky-note"></i> {entry.notes}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className={`status-badge status-${entry.status}`}>{entry.status.replace(/_/g, ' ')}</span>
                <button className="btn-icon" title="Attach evidence" onClick={() => setEvidenceEntry(entry)}>
                  <i className="fas fa-camera"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EvidenceUploader
        isOpen={!!evidenceEntry}
        onClose={() => setEvidenceEntry(null)}
        title="Evidence"
        subtitle={evidenceEntry ? `${evidenceEntry.parameter?.name || 'Entry'} — ${new Date(evidenceEntry.entryDate).toLocaleDateString()}` : ''}
        reloadKey={evidenceEntry?.id}
        fetchEvidence={async () => {
          const data = await monitoringService.getEntryEvidence(evidenceEntry.id);
          return data.evidence;
        }}
        uploadEvidence={async (file, fileType) => {
          const data = await monitoringService.uploadEntryEvidence(evidenceEntry.id, file, fileType);
          return data.evidence;
        }}
        deleteEvidence={(evidenceId) => monitoringService.deleteEvidence(evidenceId)}
      />

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-clipboard-list"></i> Add Daily Log Entry</h2>
              <button className="close-btn" onClick={closeModal}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Project *</label>
                    <select value={form.projectId} onChange={handleFieldChange('projectId')} required>
                      <option value="">Select project...</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Parameter *</label>
                    <select value={form.parameterId} onChange={handleFieldChange('parameterId')} required>
                      <option value="">Select parameter...</option>
                      {availableParameters.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} ({formatCategory(p.category)})</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Date *</label>
                    <DatePickerField value={form.entryDate} onChange={handleFieldChange('entryDate')} maxDate={todayIso()} />
                  </div>

                  {selectedParameter?.dataType === 'number' && (
                    <div className="form-group full-width">
                      <label>Value {selectedParameter.unit ? `(${selectedParameter.unit})` : ''} *</label>
                      <input type="number" step="any" value={form.numberValue} onChange={handleFieldChange('numberValue')} required />
                    </div>
                  )}

                  {selectedParameter?.dataType === 'boolean' && (
                    <div className="form-group full-width">
                      <label>Value *</label>
                      <select value={form.booleanValue} onChange={handleFieldChange('booleanValue')}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  )}

                  {selectedParameter?.dataType === 'select' && (
                    <div className="form-group full-width">
                      <label>Value *</label>
                      <select value={form.selectValue} onChange={handleFieldChange('selectValue')} required>
                        <option value="">Select...</option>
                        {(selectedParameter.options || []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {(!selectedParameter || selectedParameter.dataType === 'text') && (
                    <div className="form-group full-width">
                      <label>Value *</label>
                      <input type="text" value={form.textValue} onChange={handleFieldChange('textValue')} placeholder="Enter observation" required={!!selectedParameter} />
                    </div>
                  )}

                  <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea rows="2" value={form.notes} onChange={handleFieldChange('notes')} placeholder="Optional context" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary-green" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyLog;
